import { computed } from "vue";
import { useFuse } from "@vueuse/integrations/useFuse";
import { FUSE_OPTIONS, MIN_FUZZY_QUERY_LENGTH, rankFuzzyItems } from "./rank-fuzzy-items.js";
import { filterBySearch } from "./filter-items.js";
import { groupItems } from "./group-items.js";
import { markFirstResult } from "./mark-first-result.js";
import { keywordList } from "./search-helpers.js";

/**
 * Search + group pipeline for navigation containers. Routes a query to one of two
 * matchers and returns `groupedItems`: the fuzzy pipeline (an exact word-prefix
 * gate, falling back to fuse.js + a deterministic re-rank only when nothing
 * matches exactly) in fuzzy mode above the minimum match length, otherwise
 * substring filtering followed by section-header grouping.
 *
 * @param {Object} options
 * @param {import('vue').Ref<Array>} options.items
 * @param {import('vue').Ref<string|undefined>} options.search
 * @param {import('vue').Ref<boolean>} options.noFilter - pre-coerced; callers normalise the prop
 * @param {(item: any) => string|undefined} options.getItemTitle
 * @param {import('vue').Ref<number|string|undefined>} [options.maxResults] - cap on
 *   the number of matched (non-header) items rendered for a query. Undefined/0 = no
 *   cap. Guards against pathological renders when a huge list meets a broad query.
 * @param {import('vue').Ref<boolean>} [options.fuzzySearch] - pre-coerced; switches
 *   the query filter to the fuzzy pipeline above.
 * @param {import('vue').Ref<boolean>} [options.highlightFirstResult] - pre-coerced;
 *   while a query is active, mark the first selectable result as the combobox's
 *   pending choice (`props.highlighted`, rendered by sp-list-item as the
 *   hover-style hint). The host pairs this with committing that row on Enter.
 */
export function useQuantumSelectItems({
  items,
  search,
  noFilter,
  getItemTitle,
  maxResults,
  fuzzySearch,
  highlightFirstResult,
}) {
  // fuse indexes the searchable entries only in fuzzy mode; the non-fuzzy pickers
  // build nothing. Header and pinned rows never enter the index. Each item
  // contributes one entry per searchable string (title + each keyword) rather
  // than one weighted multi-key entry: fuse's weighted composite inflates a
  // keyword-only match's score past the legacy cutoffs (a 0.33 raw keyword match
  // scores 0.72 composite), while per-string entries score every string the way
  // the legacy autocomplete scored `matchValue`. The item's best string wins.
  const searchEntries = computed(() => {
    if (!fuzzySearch?.value) {
      return [];
    }
    return (items.value ?? [])
      .filter((item) => item && !item.header && !item.pinned)
      .flatMap((item) => {
        const title = getItemTitle(item) ?? "";
        return [title, ...keywordList(item)].map((text) => ({ item, title, text }));
      });
  });

  // All-digit queries never fuzzy-match: an off-by-one digit is one edit away
  // and would surface the wrong record. Fuse sees an empty query; the numeric
  // path in `rankFuzzyItems` matches by substring instead. Queries below the
  // fuzzy minimum also blank out: they route to substring filtering, so a fuse
  // scan would produce results nothing consumes.
  const fuseQuery = computed(() => {
    const trimmed = (search.value ?? "").trim();
    if (trimmed.length < MIN_FUZZY_QUERY_LENGTH || /^\d+$/.test(trimmed)) {
      return "";
    }
    return trimmed;
  });

  const { results: fuseResults } = useFuse(fuseQuery, searchEntries, {
    fuseOptions: FUSE_OPTIONS,
    matchAllWhenSearchEmpty: false,
  });

  const groupedItems = computed(() => {
    // Query activity is derived from the trimmed value, so a whitespace-only query
    // keeps the idle grouped list rather than emptying it through the fuzzy path.
    const query = (search.value ?? "").trim();
    let result;
    // Fuzzy ranking needs at least fuse's min match length; a shorter query falls
    // back to substring filtering so a single character still lists matches.
    if (fuzzySearch?.value && !noFilter.value && query.length >= MIN_FUZZY_QUERY_LENGTH) {
      result = rankFuzzyItems(items.value ?? [], query, fuseResults.value, getItemTitle, maxResults?.value);
    } else {
      const filtered = filterBySearch(items.value ?? [], query, noFilter.value, getItemTitle, maxResults?.value);
      result = groupItems(filtered, getItemTitle);
    }

    if (highlightFirstResult?.value && query) {
      result = markFirstResult(result);
    }
    return result;
  });

  return { groupedItems };
}
